import React from 'react';
import { styled, keyframes, css } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import Badge from '@atlaskit/badge';
import Heading from '@atlaskit/heading';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery } from '@atlassian/jira-relay/src/__generated__/ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery.graphql';
import {
	CollapsibleSection,
	CollapsibleToggleButton,
} from '@atlassian/jira-software-releases-collapsible-ui-toggle/src/ui/index.tsx';
import { DesignCardSkeleton } from '../common/ui/design-card-skeleton/DesignCardSkeleton.tsx';
import { IssueAssociatedDesignsExperienceSuccess } from '../services/experience-tracker/IssueAssociatedDesignsExperienceStart.tsx';
import { DesignCarousel } from './design-carousel/index.tsx';
import { EmptyState } from './empty-state/EmptyState.tsx';
import messages from './messages.tsx';

type Props = {
	queryRef: PreloadedQuery<ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery>;
};

export const IssueAssociatedDesigns = ({ queryRef }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const {
		jira: { version },
	} = usePreloadedQuery<ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query ui_versionIssueAssociatedDesigns_IssueAssociatedDesignsQuery(
				$cloudAri: ID!
				$versionAri: ID!
			) {
				jira @required(action: THROW) {
					version(id: $versionAri) @required(action: THROW) {
						__typename
						... on JiraVersion {
							...ui_softwareReleasesCollapsibleUiToggle_CollapsibleSection
							...designCarousel_versionIssueAssociatedDesigns
							issueAssociatedDesigns(
								first: 6
								sort: { createdAt: { direction: DESC, priority: 1 } }
							) @required(action: THROW) {
								totalCount @required(action: THROW)
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	// In real world this shouldn't happen, since this query won't be even executed when this happen by version detail's page main query
	if (version.__typename !== 'JiraVersion')
		throw new Error('Version query result is not VersionResult');

	const { totalCount } = version.issueAssociatedDesigns;

	return (
		<ContextualAnalyticsData
			sourceName="versionIssueAssociatedDesigns"
			sourceType={SCREEN}
			attributes={{ designsCount: totalCount }}
		>
			<CollapsibleSection
				jiraVersion={version}
				renderBody={() => {
					if (totalCount === 0) {
						return <EmptyState />;
					}
					return <DesignCarousel jiraVersion={version} />;
				}}
				renderHeader={(isCollapsed, onToggleClicked) => (
					<HeaderWrapper onClick={onToggleClicked}>
						<Heading size="small" as="h2">
							{formatMessage(messages.designSectionLabel)}
						</Heading>
						<Badge>{totalCount}</Badge>
						<CollapsibleToggleButton isCollapsed={isCollapsed} onClick={onToggleClicked} />
					</HeaderWrapper>
				)}
				uiType="ISSUE_ASSOCIATED_DESIGNS"
			/>
			<IssueAssociatedDesignsExperienceSuccess />
			<FireScreenAnalytics />
		</ContextualAnalyticsData>
	);
};

export const DesignSectionSkeleton = (): React.JSX.Element => (
	<Box>
		<SkeletonItem width="220px" height="24px" margin="44px 0 20px 0" />
		<Inline xcss={designCardsSkeletonWrapperStyles}>
			<DesignCardSkeleton />
			<DesignCardSkeleton />
			<DesignCardSkeleton />
			<DesignCardSkeleton />
			<DesignCardSkeleton />
			<DesignCardSkeleton />
		</Inline>
		<Inline xcss={crouselCounterStyles}>
			<SkeletonItem width="100px" height="20px" margin="20px 0 0 0" />
		</Inline>
	</Box>
);

interface SkeletonItemProps {
	height: string;
	width: string;
	margin: string;
}

const designCardsSkeletonWrapperStyles = xcss({
	gap: 'space.200',
	width: '100%',
	overflow: 'hidden',
	padding: 'space.050',
	marginTop: 'space.100',
});

const crouselCounterStyles = xcss({
	justifyContent: 'center',
	alignItems: 'center',
	height: '20px',
});

const animationOptionStyles = css({
	animationDelay: "'0s'",
	animationDuration: '2s',
	animationFillMode: 'forwards',
	animationIterationCount: 'infinite',
	animationTimingFunction: 'linear',
	backgroundImage: `linear-gradient( to right, ${token('color.skeleton')} 10%, ${token('color.skeleton.subtle')} 30%, ${token('color.skeleton')} 50% )`,
	backgroundRepeat: 'no-repeat',
});

const shimmer = keyframes({
	'0%': {
		backgroundPosition: '-300px 0',
	},
	'100%': {
		backgroundPosition: '1700px 0',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const SkeletonItem = styled.div<SkeletonItemProps>(animationOptionStyles, {
	animationName: shimmer,

	backgroundColor: token('color.skeleton'),
	borderRadius: token('radius.small'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props: SkeletonItemProps) => props.height,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props: SkeletonItemProps) => props.width,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	margin: (props: SkeletonItemProps) => `${props.margin}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeaderWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	justifyContent: 'flex-start',
	gap: token('space.100'),
	cursor: 'pointer',
	marginTop: token('space.500'),
});
